import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/** -----------------------------
 * Generic helper to follow Learn REST paging (results + paging.nextPage)
 * ----------------------------*/
const fetchPagedResults = async (platformUrl, initialPath, headers) => {
    let results = [];
    let nextPath = initialPath;

    while (nextPath) {
        const resp = await axios.get(`${platformUrl}${nextPath}`, { headers });
        const data = resp.data || {};

        if (Array.isArray(data.results)) {
            results = results.concat(data.results);
        }

        const paging = data.paging || {};
        nextPath = paging.nextPage ? paging.nextPage : null;
    }

    return results;
};

const normalizeBase = (u) => String(u || "").trim().replace(/\/+$/, "");
const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

/** -----------------------------
 * Learn API wrappers (read-only)
 * ----------------------------*/
const listCoursesByDataSource = async (platformUrl, dataSourceIdQuery, headers) => {
    const base = normalizeBase(platformUrl);

    // Course limit updated to 100
    const url = `${base}/learn/api/public/v3/courses`;
    const resp = await axios.get(url, {
        headers,
        params: {
            dataSourceId: dataSourceIdQuery,
            limit: 100,
        },
    });

    const data = resp.data || {};
    return Array.isArray(data.results) ? data.results : [];
};

const getCourseMemberships = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v1/courses/${encodeURIComponent(courseRef)}/users?limit=200&expand=user`,
        headers
    );
};

const getGradebookColumns = async (platformUrl, courseRef, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v2/courses/${encodeURIComponent(courseRef)}/gradebook/columns?limit=200`,
        headers
    );
};

const getColumnUserGrades = async (platformUrl, courseRef, columnId, headers) => {
    return fetchPagedResults(
        platformUrl,
        `/learn/api/public/v2/courses/${encodeURIComponent(courseRef)}/gradebook/columns/${encodeURIComponent(
            columnId
        )}/users?limit=200`,
        headers
    );
};

/** -----------------------------
 * Status + submission detection (best-effort)
 * ----------------------------*/
const deriveSubmissionStatus = (gradeRow) => {
    if (!gradeRow) return "No record";

    const attemptStatus =
        gradeRow.attemptStatus ||
        gradeRow.status ||
        (gradeRow.attempt && gradeRow.attempt.status) ||
        null;

    const hasDisplayGrade = !!gradeRow.displayGrade;
    const hasScore = gradeRow.score !== undefined && gradeRow.score !== null;

    if (attemptStatus) return safeStr(attemptStatus);
    if (hasDisplayGrade || hasScore) return "Submitted/graded";
    return "No record";
};

const hasSubmissionEvidence = (gradeRow) => {
    if (!gradeRow) return false;

    const attemptStatus =
        gradeRow.attemptStatus ||
        gradeRow.status ||
        (gradeRow.attempt && gradeRow.attempt.status) ||
        null;

    const hasDisplayGrade = !!gradeRow.displayGrade;
    const hasScore = gradeRow.score !== undefined && gradeRow.score !== null;

    const hasAttemptObj = !!gradeRow.attempt;
    const hasAttemptId = !!gradeRow.attemptId;

    return Boolean(attemptStatus || hasDisplayGrade || hasScore || hasAttemptObj || hasAttemptId);
};

const isAvailableYes = (x) => safeStr(x?.availability?.available || "Yes").toLowerCase() === "yes";
const isStudentMembership = (m) => safeStr(m?.courseRoleId).toLowerCase() === "student";

/** -----------------------------
 * Component
 * ----------------------------*/
const TrainingActivitySimulator = ({ platformUrl, backEndUrl, tenantId }) => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const userUuid = params.get("uuid"); // REQUIRED for backend lambda query params

    const [dataSourceIdQuery, setDataSourceIdQuery] = useState("externalId:TRAINING");

    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState("");

    const selectedCourse = useMemo(
        () => courses.find((c) => c.id === selectedCourseId) || null,
        [courses, selectedCourseId]
    );

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [memberships, setMemberships] = useState([]);
    const [columns, setColumns] = useState([]);

    // Cache: colId -> grade rows array (only fetched for visible page)
    const [columnGrades, setColumnGrades] = useState({});
    // Track loading states per column for nicer UI
    const [columnGradesLoading, setColumnGradesLoading] = useState({}); // colId -> boolean

    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [error, setError] = useState(null);

    // Apply flow
    const [oneTimeKey, setOneTimeKey] = useState("");
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [applyResult, setApplyResult] = useState(null);

    // Assignment paging
    const [assignmentPageIndex, setAssignmentPageIndex] = useState(0);
    const [assignmentPageSize, setAssignmentPageSize] = useState(10);

    // Nonce: increments whenever we begin loading a new course, so we can ignore stale async responses
    const courseLoadNonceRef = useRef(0);

    const loadCourses = async () => {
        setError(null);
        setApplyError(null);
        setApplyResult(null);

        try {
            if (!token) throw new Error("Missing token in URL.");
            setLoadingCourses(true);

            const list = await listCoursesByDataSource(platformUrl, dataSourceIdQuery, headers);
            setCourses(list);
            setSelectedCourseId(list[0]?.id || "");
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Failed to load courses.");
        } finally {
            setLoadingCourses(false);
        }
    };

    const loadCourseDetail = async (course) => {
        if (!course) return;

        // bump nonce: invalidates any in-flight work for the prior course
        const myNonce = ++courseLoadNonceRef.current;

        setError(null);
        setApplyError(null);
        setApplyResult(null);

        // IMPORTANT: clear old course detail immediately to prevent stale paging fetches
        setLoadingDetail(true);
        setAssignmentPageIndex(0);
        setMemberships([]);
        setColumns([]);
        setColumnGrades({});
        setColumnGradesLoading({});

        try {
            const courseRef = course.id;

            const mem = await getCourseMemberships(platformUrl, courseRef, headers);
            if (courseLoadNonceRef.current !== myNonce) return; // course changed mid-flight
            setMemberships(mem);

            const cols = await getGradebookColumns(platformUrl, courseRef, headers);
            if (courseLoadNonceRef.current !== myNonce) return;
            setColumns(cols);
        } catch (e) {
            if (courseLoadNonceRef.current !== myNonce) return;
            setError(e?.response?.data?.message || e?.message || "Failed to load course details.");
        } finally {
            if (courseLoadNonceRef.current === myNonce) {
                setLoadingDetail(false);
            }
        }
    };

    // Initial load
    useEffect(() => {
        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load detail when selection changes
    useEffect(() => {
        if (!selectedCourse) return;
        loadCourseDetail(selectedCourse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourseId]);

    const students = useMemo(() => {
        return memberships
            .filter(isAvailableYes)
            .filter(isStudentMembership)
            .map((m) => {
                const u = m.user || {};
                const name = `${safeStr(u?.name?.given)} ${safeStr(u?.name?.family)}`.trim();
                return {
                    membershipId: m.id,
                    userId: m.userId,
                    userUuid: u.uuid,
                    userName: u.userName,
                    displayName: name || u.userName || m.userId,
                };
            });
    }, [memberships]);

    const attemptColumnsAll = useMemo(() => {
        return columns
            .filter((c) => isAvailableYes(c))
            .filter((c) => safeStr(c?.grading?.type).toLowerCase() === "attempts")
            .map((c) => ({
                id: c.id,
                name: c.name,
                due: c?.grading?.due || null,
                possible: c?.score?.possible ?? null,
            }));
    }, [columns]);

    const assignmentTotalPages = useMemo(() => {
        const total = attemptColumnsAll.length;
        const size = Math.max(1, assignmentPageSize);
        return Math.max(1, Math.ceil(total / size));
    }, [attemptColumnsAll.length, assignmentPageSize]);

    // Keep page index in range if page size changes or list shrinks
    useEffect(() => {
        if (assignmentPageIndex > assignmentTotalPages - 1) {
            setAssignmentPageIndex(assignmentTotalPages - 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentTotalPages]);

    const attemptColumnsPage = useMemo(() => {
        const size = Math.max(1, assignmentPageSize);
        const start = assignmentPageIndex * size;
        return attemptColumnsAll.slice(start, start + size);
    }, [attemptColumnsAll, assignmentPageIndex, assignmentPageSize]);

    const gradeRowByColumnAndUser = useMemo(() => {
        const out = {};
        for (const col of attemptColumnsPage) {
            const rows = columnGrades[col.id] || [];
            const m = {};
            for (const r of rows) {
                if (r && r.userId) m[r.userId] = r;
            }
            out[col.id] = m;
        }
        return out;
    }, [attemptColumnsPage, columnGrades]);

    const submissionCountByColumnId = useMemo(() => {
        const out = {};
        for (const a of attemptColumnsPage) {
            const userMap = gradeRowByColumnAndUser[a.id] || {};
            let count = 0;

            for (const s of students) {
                const row = userMap[s.userId] || null;
                if (hasSubmissionEvidence(row)) count++;
            }

            out[a.id] = count;
        }
        return out;
    }, [attemptColumnsPage, gradeRowByColumnAndUser, students]);

    const ensureGradeRowsLoadedForPage = async () => {
        if (!selectedCourse) return;
        if (!token) return;

        const courseRef = selectedCourse.id;

        // capture these at call-time to prevent stale mixing after a course switch
        const myNonce = courseLoadNonceRef.current;
        const myCourseId = selectedCourseId;

        const toLoad = attemptColumnsPage
            .map((c) => c.id)
            .filter((colId) => !columnGrades[colId] && !columnGradesLoading[colId]);

        if (toLoad.length === 0) return;

        // Mark as loading
        setColumnGradesLoading((prev) => {
            const next = { ...prev };
            for (const id of toLoad) next[id] = true;
            return next;
        });

        try {
            for (const colId of toLoad) {
                // if course changed while looping, stop immediately
                if (courseLoadNonceRef.current !== myNonce || selectedCourseId !== myCourseId) return;

                const rows = await getColumnUserGrades(platformUrl, courseRef, colId, headers);

                if (courseLoadNonceRef.current !== myNonce || selectedCourseId !== myCourseId) return;

                setColumnGrades((prev) => ({ ...prev, [colId]: rows }));
                setColumnGradesLoading((prev) => ({ ...prev, [colId]: false }));
            }
        } catch (e) {
            // If stale, ignore
            if (courseLoadNonceRef.current !== myNonce || selectedCourseId !== myCourseId) return;

            setColumnGradesLoading((prev) => {
                const next = { ...prev };
                for (const id of toLoad) next[id] = false;
                return next;
            });

            setError(e?.response?.data?.message || e?.message || "Failed to load submission rows for assignments.");
        }
    };

    // Load grade rows for the currently displayed assignment page
    useEffect(() => {
        if (!selectedCourse) return;
        if (!token) return;
        if (attemptColumnsPage.length === 0) return;

        ensureGradeRowsLoadedForPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourseId, assignmentPageIndex, assignmentPageSize, attemptColumnsPage.length]);

    const handleGenerateRandomActivity = async () => {
        setApplyError(null);
        setApplyResult(null);

        try {
            // Keep tenantId prop around, but the endpoint does not require it right now
            if (!tenantId) throw new Error("Missing tenantId prop.");
            if (!backEndUrl) throw new Error("Missing backendUrl prop.");
            if (!token) throw new Error("Missing token in URL.");
            if (!userUuid) throw new Error("Missing userUuid in URL.");
            if (!selectedCourse) throw new Error("No course selected.");
            if (!oneTimeKey.trim()) throw new Error("Enter the one-time key.");

            setApplying(true);

            const url = `${normalizeBase(backEndUrl)}/training/generate-random-activity`;

            const resp = await axios.post(
                url,
                {
                    courseId: selectedCourse.id,
                    maxAssignments: 5,
                    maxStudentsPerAssignment: 10,
                    outcomes: { onTime: 0.55, late: 0.25, missing: 0.2 },
                    attachments: { probability: 0.35 },
                },
                {
                    params: {
                        platformUrl: normalizeBase(platformUrl),
                        userUuid,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "X-One-Time-Key": oneTimeKey.trim(),
                        "X-Tenant-Id": tenantId,
                        "Content-Type": "application/json",
                    },
                }
            );

            setApplyResult(resp.data || { ok: true });

            // refresh read-only view afterward
            await loadCourseDetail(selectedCourse);
        } catch (e) {
            setApplyError(
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                e?.message ||
                "Failed to generate random activity."
            );
        } finally {
            setApplying(false);
        }
    };

    const canPrevAssignments = assignmentPageIndex > 0;
    const canNextAssignments = assignmentPageIndex < assignmentTotalPages - 1;

    return (
        <div className="bb-container">
            <h2>Training Activity Simulator</h2>

            {!token && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    Missing token in URL. This tool must be launched via LTI with a token parameter.
                </div>
            )}

            {!userUuid && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    Missing userUuid in URL. This tool must be launched via LTI with a userUuid parameter.
                </div>
            )}

            {error && (
                <div className="bb-error" style={{ color: "red", marginBottom: "0.75rem" }}>
                    {error}
                </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", marginBottom: "1rem" }}>
                <div style={{ minWidth: 380 }}>
                    <label>
                        <strong>Data Source Selector (course query)</strong>
                    </label>
                    <input
                        style={{ width: "100%" }}
                        value={dataSourceIdQuery}
                        onChange={(e) => setDataSourceIdQuery(e.target.value)}
                        placeholder="e.g., externalId:TRAINING"
                    />
                    <div style={{ fontSize: "0.85rem", opacity: 0.8, marginTop: "0.25rem" }}>
                        Used for <code>/learn/api/public/v3/courses?dataSourceId=...</code>
                    </div>
                </div>

                <button className="bb-button" onClick={loadCourses} disabled={loadingCourses || !token}>
                    {loadingCourses ? "Loading courses..." : "Load courses"}
                </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "1rem" }}>
                {/* Left panel: Courses + Apply */}
                <div>
                    <h3>Courses ({courses.length})</h3>

                    <select
                        style={{ width: "100%" }}
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                        disabled={courses.length === 0}
                    >
                        {courses.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name} ({c.courseId})
                            </option>
                        ))}
                    </select>

                    {selectedCourse && (
                        <div style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                            <div>
                                <strong>Selected:</strong> {selectedCourse.name}
                            </div>
                            <div>
                                <strong>CourseId:</strong> {selectedCourse.courseId}
                            </div>
                            <div>
                                <strong>Internal Id:</strong> {selectedCourse.id}
                            </div>
                            <div>
                                <strong>Course dataSourceId:</strong> {selectedCourse.dataSourceId}
                            </div>
                            {loadingDetail && <div style={{ marginTop: "0.5rem" }}>Loading course detail…</div>}
                        </div>
                    )}

                    <hr style={{ margin: "1rem 0" }} />

                    <h3>Generate Random Activity</h3>
                    <div style={{ fontSize: "0.85rem", opacity: 0.85, marginBottom: "0.5rem" }}>
                        Enter a one-time key issued by your backend. The backend validates the key and enforces safeguards.
                    </div>

                    <input
                        style={{ width: "100%", marginBottom: "0.5rem" }}
                        value={oneTimeKey}
                        onChange={(e) => setOneTimeKey(e.target.value)}
                        placeholder="One-time key"
                    />

                    <button
                        className="bb-button"
                        onClick={handleGenerateRandomActivity}
                        disabled={applying || !selectedCourse || !token || !userUuid}
                    >
                        {applying ? "Generating..." : "Generate random activity"}
                    </button>

                    {applyError && <div style={{ color: "red", marginTop: "0.5rem" }}>{applyError}</div>}
                    {applyResult && (
                        <pre
                            style={{
                                marginTop: "0.75rem",
                                background: "#f6f6f6",
                                padding: "0.5rem",
                                overflowX: "auto",
                            }}
                        >
                            {JSON.stringify(applyResult, null, 2)}
                        </pre>
                    )}
                </div>

                {/* Right panel: Enrollments + Assignments + Status */}
                <div>
                    <h3>
                        Course Detail {loadingDetail && <span style={{ fontSize: "0.9rem" }}>(loading...)</span>}
                    </h3>

                    <div style={{ marginBottom: "1rem" }}>
                        <strong>Enrollments:</strong> {memberships.length} (students: {students.length})
                    </div>

                    <h4>Students</h4>
                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Username</th>
                            <th>UserId</th>
                        </tr>
                        </thead>
                        <tbody>
                        {students.map((s) => (
                            <tr key={s.userId}>
                                <td>{s.displayName}</td>
                                <td>{s.userName || "-"}</td>
                                <td>{s.userId}</td>
                            </tr>
                        ))}
                        {students.length === 0 && (
                            <tr>
                                <td colSpan={3}>No student enrollments found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Attempt-based Assignments (Gradebook Columns)</h4>

                    {/* Assignment paging controls */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            gap: "0.75rem",
                            marginBottom: "0.5rem",
                        }}
                    >
                        <div style={{ fontSize: "0.9rem" }}>
                            <strong>Total assignments:</strong> {attemptColumnsAll.length}{" "}
                            {attemptColumnsAll.length > 0 && (
                                <span style={{ opacity: 0.85 }}>
                                    (Page {assignmentPageIndex + 1} of {assignmentTotalPages})
                                </span>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
                            <div>
                                <label style={{ fontSize: "0.85rem" }}>Page size</label>
                                <select
                                    value={assignmentPageSize}
                                    onChange={(e) => {
                                        const n = Number(e.target.value);
                                        setAssignmentPageSize(Number.isFinite(n) && n > 0 ? n : 10);
                                        setAssignmentPageIndex(0);
                                    }}
                                    style={{ marginLeft: "0.5rem" }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={25}>25</option>
                                </select>
                            </div>

                            <button
                                className="bb-button"
                                onClick={() => setAssignmentPageIndex((p) => Math.max(0, p - 1))}
                                disabled={!canPrevAssignments || attemptColumnsAll.length === 0}
                            >
                                Prev
                            </button>
                            <button
                                className="bb-button"
                                onClick={() =>
                                    setAssignmentPageIndex((p) => Math.min(assignmentTotalPages - 1, p + 1))
                                }
                                disabled={!canNextAssignments || attemptColumnsAll.length === 0}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <table className="bb-table" style={{ width: "100%", marginBottom: "1rem" }}>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Due</th>
                            <th>Points</th>
                            <th>Submissions</th>
                            <th>ColumnId</th>
                        </tr>
                        </thead>
                        <tbody>
                        {attemptColumnsPage.map((a) => {
                            const isLoading = !!columnGradesLoading[a.id];
                            const loaded = !!columnGrades[a.id];
                            const submissions = submissionCountByColumnId[a.id];

                            return (
                                <tr key={a.id}>
                                    <td>{a.name}</td>
                                    <td>{a.due || "-"}</td>
                                    <td>{a.possible ?? "-"}</td>
                                    <td>
                                        {students.length === 0
                                            ? "0 / 0"
                                            : isLoading
                                                ? "Loading..."
                                                : loaded
                                                    ? `${submissions} / ${students.length}`
                                                    : "-"}
                                    </td>
                                    <td>{a.id}</td>
                                </tr>
                            );
                        })}
                        {attemptColumnsAll.length === 0 && (
                            <tr>
                                <td colSpan={5}>No attempt-based columns found.</td>
                            </tr>
                        )}
                        {attemptColumnsAll.length > 0 && attemptColumnsPage.length === 0 && (
                            <tr>
                                <td colSpan={5}>No assignments on this page.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>

                    <h4>Submission Status (for displayed assignments)</h4>
                    <div style={{ fontSize: "0.85rem", opacity: 0.8, marginBottom: "0.5rem" }}>
                        Status is derived from grade/attempt fields returned by the gradebook user-rows endpoint. Only the
                        assignments shown on the current page are queried for per-user rows.
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table className="bb-table" style={{ width: "100%" }}>
                            <thead>
                            <tr>
                                <th>Student</th>
                                {attemptColumnsPage.map((a) => (
                                    <th key={a.id}>{a.name}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {students.map((s) => (
                                <tr key={s.userId}>
                                    <td>{s.displayName}</td>
                                    {attemptColumnsPage.map((a) => {
                                        const isLoading = !!columnGradesLoading[a.id];
                                        const row = gradeRowByColumnAndUser[a.id]?.[s.userId] || null;
                                        return <td key={a.id}>{isLoading ? "…" : deriveSubmissionStatus(row)}</td>;
                                    })}
                                </tr>
                            ))}
                            {students.length === 0 && (
                                <tr>
                                    <td colSpan={1 + attemptColumnsPage.length}>No students.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrainingActivitySimulator;